<template>
  <!-- 구매자가 볼 상세. embedded=true 면 우측 패널용(오버레이·닫기 없음) -->
  <div
    class="spv"
    :class="{ 'spv--embedded': embedded }"
    :role="embedded ? 'region' : 'dialog'"
    :aria-modal="embedded ? undefined : true"
    aria-label="상품 상세 미리보기"
    @click.self="!embedded && emit('close')"
  >
    <div class="spv__panel">
      <header v-if="!embedded" class="spv__head">
        <h2>
          상세 미리보기
          <em>구매 기관에게 이렇게 보입니다 · 영역을 누르면 입력칸으로 이동합니다</em>
        </h2>
        <button type="button" class="spv__close" aria-label="닫기" @click="emit('close')">
          <AppIcon icon="mdi-close" :size="20" />
        </button>
      </header>
      <p v-else class="spv__embed-hint">
        아래 영역을 누르면 왼쪽 입력칸으로 이동합니다. 입력칸을 누르면 여기가 강조됩니다.
      </p>

      <div ref="scrollEl" class="spv__scroll">
        <div class="spv__top">
          <div
            class="spv__zone"
            data-zone="gallery"
            :class="{ 'is-on': isOn('gallery') || isOn('badge') }"
            role="button"
            tabindex="0"
            @click="emitEdit('gallery')"
            @keydown.enter.prevent="emitEdit('gallery')"
          >
            <span class="spv__zone-label">이미지 · 배지</span>
            <div class="spv__gallery">
              <div class="spv__main">
                <img v-if="activeImage" :src="activeImage" :alt="product.name" />
                <div v-else class="spv__main-empty">
                  <AppIcon icon="mdi-image-outline" :size="34" />
                  <span>이미지를 등록하면 여기에 보입니다</span>
                </div>
                <em
                  v-if="product.badge"
                  class="spv__badge"
                  :class="{ 'is-on': isOn('badge') }"
                  @click.stop="emitEdit('badge')"
                >
                  {{ product.badge }}
                </em>
              </div>
              <ul v-if="images.length > 1" class="spv__thumbs">
                <li v-for="(image, index) in images" :key="image">
                  <button
                    type="button"
                    :class="{ 'is-active': index === activeIndex }"
                    @click.stop="activeIndex = index"
                  >
                    <img :src="image" :alt="`${product.name} 이미지 ${index + 1}`" />
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div class="spv__buy">
            <div
              class="spv__zone"
              data-zone="cate"
              :class="{ 'is-on': isOn('cate') }"
              role="button"
              tabindex="0"
              @click="emitEdit('cate')"
              @keydown.enter.prevent="emitEdit('cate')"
            >
              <span class="spv__zone-label">분류</span>
              <p class="spv__store">
                <AppIcon icon="mdi-storefront-outline" :size="15" />
                {{ product.storeName || '내 스토어' }}
                <span class="spv__cat">{{ product.category }}</span>
              </p>
            </div>

            <div
              class="spv__zone"
              data-zone="name"
              :class="{ 'is-on': isOn('name') }"
              role="button"
              tabindex="0"
              @click="emitEdit('name')"
              @keydown.enter.prevent="emitEdit('name')"
            >
              <span class="spv__zone-label">상품명 · 단위</span>
              <h3 class="spv__name">{{ product.name || '상품명 없음' }}</h3>
            </div>

            <div
              class="spv__zone"
              data-zone="summary"
              :class="{ 'is-on': isOn('summary') }"
              role="button"
              tabindex="0"
              @click="emitEdit('summary')"
              @keydown.enter.prevent="emitEdit('summary')"
            >
              <span class="spv__zone-label">한 줄 요약</span>
              <p class="spv__summary">{{ product.summary || '요약을 입력하면 여기에 보입니다.' }}</p>
            </div>

            <div
              class="spv__zone"
              data-zone="price"
              :class="{ 'is-on': isOn('price') }"
              role="button"
              tabindex="0"
              @click="emitEdit('price')"
              @keydown.enter.prevent="emitEdit('price')"
            >
              <span class="spv__zone-label">가격</span>
              <div class="spv__price">
                <strong>{{ formatPrice(isGroupBuy ? priced.unitPrice * cartonPieces : offer.price) }}</strong>
                <span>원</span>
                <span class="spv__unit-tag">{{ isGroupBuy ? '/카톤' : offer.name || (sellNoun === '박스' ? '/박스' : '') }}</span>
              </div>
              <p v-if="isGroupBuy" class="spv__photo">{{ cartonPieces }}개 묶음 · 1개당 {{ formatPrice(priced.unitPrice) }}원</p>
              <p class="spv__photo">{{ imageCaption }}</p>
              <dl class="spv__info">
                <div>
                  <dt>판매</dt>
                  <dd>{{ isGroupBuy ? `${cartonPieces}개 묶음` : sellSummary }}</dd>
                </div>
                <div v-if="product.unit">
                  <dt>구성</dt>
                  <dd>{{ product.unit }}</dd>
                </div>
              </dl>
            </div>

            <div
              class="spv__zone"
              data-zone="stock"
              :class="{ 'is-on': isOn('stock') }"
              role="button"
              tabindex="0"
              @click="emitEdit('stock')"
              @keydown.enter.prevent="emitEdit('stock')"
            >
              <span class="spv__zone-label">재고 · 주문</span>
              <dl class="spv__info">
                <div v-if="isGroupBuy">
                  <dt>주문</dt>
                  <dd>최소 1카톤</dd>
                </div>
                <div v-else-if="isOrderPack(product.orderStep)">
                  <dt>주문</dt>
                  <dd>
                    <MallOrderPackMark
                      size="inline"
                      :order-step="product.orderStep || 1"
                      :sell-unit="product.sellUnit"
                    />
                  </dd>
                </div>
                <div v-else-if="product.minOrder > 1">
                  <dt>최소 주문</dt>
                  <dd>{{ minOrderText }}</dd>
                </div>
                <div>
                  <dt>재고</dt>
                  <dd :class="{ 'is-warn': (product.stock ?? 0) <= 0 }">
                    {{
                      (product.stock ?? 0) > 0
                        ? formatStockCount(product.stock ?? 0, product.sellUnit)
                        : '재고 없음 (품절로 노출)'
                    }}
                  </dd>
                </div>
              </dl>
            </div>

            <div
              class="spv__zone"
              data-zone="options"
              :class="{ 'is-on': isOn('options') }"
              role="button"
              tabindex="0"
              @click="emitEdit('options')"
              @keydown.enter.prevent="emitEdit('options')"
            >
              <span class="spv__zone-label">옵션 선택</span>
              <div class="spv__options">
                <p class="spv__options-label">옵션 선택 <em>(필수)</em></p>
                <select class="spv__options-select" disabled tabindex="-1" aria-hidden="true">
                  <option
                    v-for="choice in optionChoices"
                    :key="choice.key"
                    :value="choice.key"
                  >
                    {{ choice.label }}
                  </option>
                </select>
                <ul v-if="optionChoices.length" class="spv__options-list">
                  <li v-for="choice in optionChoices" :key="`row-${choice.key}`">
                    <strong>{{ choice.name }}</strong>
                    <span v-if="choice.extra">
                      {{ choice.extra > 0 ? '+' : '' }}{{ formatPrice(choice.extra) }}원
                    </span>
                    <em :class="{ 'is-warn': choice.stock <= 0 }">
                      {{ choice.stock > 0 ? `재고 ${formatPrice(choice.stock)}` : '품절' }}
                    </em>
                  </li>
                </ul>
                <p v-else class="spv__options-empty">옵션을 만들면 상세에서 선택창으로 보입니다.</p>
              </div>
            </div>

            <div
              class="spv__zone"
              data-zone="ship"
              :class="{ 'is-on': isOn('ship') }"
              role="button"
              tabindex="0"
              @click="emitEdit('ship')"
              @keydown.enter.prevent="emitEdit('ship')"
            >
              <span class="spv__zone-label">배송</span>
              <dl class="spv__info">
                <div>
                  <dt>배송비</dt>
                  <dd>{{ shippingText }}</dd>
                </div>
                <div v-if="product.leadTime">
                  <dt>출고</dt>
                  <dd>{{ product.leadTime }}</dd>
                </div>
                <div>
                  <dt>수령</dt>
                  <dd>{{ MALL_FULFILLMENT_LIMIT }}</dd>
                </div>
                <div v-if="sameDayCutoffText">
                  <dt>당일 출고</dt>
                  <dd>{{ sameDayCutoffText }}</dd>
                </div>
                <div v-if="product.origin || product.maker">
                  <dt>원산지 · 제조</dt>
                  <dd>{{ [product.origin, product.maker].filter(Boolean).join(' · ') }}</dd>
                </div>
              </dl>
            </div>

            <div
              class="spv__zone"
              data-zone="tags"
              :class="{ 'is-on': isOn('tags') }"
              role="button"
              tabindex="0"
              @click="emitEdit('tags')"
              @keydown.enter.prevent="emitEdit('tags')"
            >
              <span class="spv__zone-label">해시태그</span>
              <ul v-if="product.tags.length" class="spv__tags">
                <li v-for="tag in product.tags" :key="tag">#{{ tag }}</li>
              </ul>
              <p v-else class="spv__empty-inline">태그를 넣으면 여기에 보입니다.</p>
            </div>

            <div class="spv__cta">
              <span class="spv__cta-cart">장바구니</span>
              <span class="spv__cta-buy">바로구매</span>
            </div>
            <p class="spv__cta-note">구매 버튼은 미리보기에서 동작하지 않습니다.</p>
          </div>
        </div>

        <section
          class="spv__section spv__zone"
          data-zone="specs"
          :class="{ 'is-on': isOn('specs') }"
          role="button"
          tabindex="0"
          @click="emitEdit('specs')"
          @keydown.enter.prevent="emitEdit('specs')"
        >
          <span class="spv__zone-label">요약정보</span>
          <h4>상품 정보</h4>
          <dl v-if="product.highlights.length" class="spv__spec">
            <div v-for="spec in product.highlights" :key="spec.label">
              <dt>{{ spec.label }}</dt>
              <dd>{{ spec.value }}</dd>
            </div>
          </dl>
          <p v-else class="spv__empty">요약정보(스펙)를 입력하면 표로 보입니다.</p>
        </section>

        <section
          class="spv__section spv__zone"
          data-zone="detail"
          :class="{ 'is-on': isOn('detail') || isOn('desc') }"
          role="button"
          tabindex="0"
          @click="emitEdit('detail')"
          @keydown.enter.prevent="emitEdit('detail')"
        >
          <span class="spv__zone-label">상품 소개 · 상세</span>
          <h4>상세 설명</h4>
          <p v-if="product.description" class="spv__desc">{{ product.description }}</p>

          <div v-if="blocks.length" class="spv__blocks">
            <template v-for="(block, index) in blocks" :key="index">
              <h5 v-if="block.type === 'HEADING'" class="spv__block-heading">{{ block.text }}</h5>
              <p v-else-if="block.type === 'TEXT'" class="spv__block-text">{{ block.text }}</p>
              <p v-else-if="block.type === 'NOTICE'" class="spv__block-notice">
                <AppIcon icon="mdi-information-outline" :size="16" />
                {{ block.text }}
              </p>
              <figure v-else class="spv__block-image">
                <img :src="block.imageUrl" :alt="block.alt || product.name" />
                <figcaption v-if="block.alt">{{ block.alt }}</figcaption>
              </figure>
            </template>
          </div>

          <p v-else-if="!product.description" class="spv__empty">
            상세 페이지 블록을 넣으면 이 자리에 순서대로 보입니다.
          </p>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import MallOrderPackMark from '@/components/mall/MallOrderPackMark.vue'
import type { ProductPreviewZone } from '@/constants/productEditor'
import { MALL_FULFILLMENT_LIMIT } from '@/constants/mall'
import { formatPrice, type MallProduct } from '@/constants/mallProducts'
import { calcMallUnitPrice } from '@/utils/mallPricing'
import { scrollWhenReady } from '@/utils/scrollIntoContainer'
import { useSameDayCutoffText } from '@/composables/useContentSnippets'
import {
  formatMinOrderLabel,
  formatShippingChargeLabel,
  isOrderPack,
  formatStockCount,
  imageShowsCaption,
  sellUnitNoun,
  sellUnitSummary,
  storefrontOffer,
} from '@/utils/mallSell'

const props = withDefaults(
  defineProps<{
    product: MallProduct
    activeZone?: ProductPreviewZone | ''
    /** 우측 패널에 넣을 때 — 오버레이·닫기 없이 스크롤만 */
    embedded?: boolean
  }>(),
  {
    activeZone: '',
    embedded: false,
  },
)

const emit = defineEmits<{
  (event: 'close'): void
  (event: 'edit-zone', zone: ProductPreviewZone): void
}>()

const activeIndex = ref(0)
const scrollEl = ref<HTMLElement | null>(null)

const images = computed(() => {
  const list = props.product.images?.length ? props.product.images : [props.product.image]
  return list.filter(Boolean)
})

const activeImage = computed(() => images.value[activeIndex.value] ?? images.value[0] ?? '')

watch(images, () => {
  activeIndex.value = 0
})

const blocks = computed(() => props.product.blocks ?? [])

const offer = computed(() => storefrontOffer(props.product))
const priced = computed(() =>
  calcMallUnitPrice({
    listPrice: props.product.listPrice,
    salePrice: props.product.price,
    orgDiscountPct: 0,
  }),
)

const shippingText = computed(() => formatShippingChargeLabel(props.product))
const isGroupBuy = computed(() => props.product.mallKind === 'SALE')
const cartonPieces = computed(() => Math.max(1, props.product.packQty || props.product.orderStep || 1))
const sellNoun = computed(() => sellUnitNoun(props.product.sellUnit))
const imageCaption = computed(() => imageShowsCaption(props.product))
const sellSummary = computed(() => sellUnitSummary(props.product))
const minOrderText = computed(() => `${formatMinOrderLabel(props.product.minOrder, props.product.sellUnit)}부터`)
const { sameDayCutoffText, loadSameDayCutoffSnippet } = useSameDayCutoffText(
  () => props.product.storeCutoff,
)

onMounted(() => {
  void loadSameDayCutoffSnippet()
})

const optionChoices = computed(() => {
  const list = (props.product.options ?? []).filter(
    (option) => option.use !== false && String(option.name || '').trim(),
  )
  const productStock = Math.max(0, Number(props.product.stock) || 0)
  if (!list.length) {
    return [
      {
        key: 'default',
        name: props.product.unit || '기본',
        label: props.product.unit || '기본',
        extra: 0,
        stock: productStock,
      },
    ]
  }

  const grouped = new Map<string, typeof list>()
  list.forEach((option) => {
    const name = option.group?.trim() || '구성'
    const rows = grouped.get(name) ?? []
    rows.push(option)
    grouped.set(name, rows)
  })
  const groups = [...grouped.values()]
  const tracks = list.some((option) => (Number(option.stock) || 0) > 0)
  const stockOf = (stock: number) => (tracks ? Math.max(0, stock) : productStock)
  const labelOf = (name: string, extra: number) =>
    name + (extra ? ` (${extra > 0 ? '+' : ''}${formatPrice(extra)}원)` : '')

  if (groups.length === 1) {
    return groups[0].map((option, index) => {
      const extra = Number(option.extra) || 0
      return {
        key: `${option.group}-${option.name}-${index}`,
        name: option.name.trim(),
        label: labelOf(option.name.trim(), extra),
        extra,
        stock: stockOf(Number(option.stock) || 0),
      }
    })
  }

  let combos = groups[0].map((option) => ({
    parts: [option.name.trim()],
    extra: Number(option.extra) || 0,
    stock: Number(option.stock) || 0,
  }))
  groups.slice(1).forEach((group) => {
    const next: typeof combos = []
    combos.forEach((combo) => {
      group.forEach((option) => {
        next.push({
          parts: [...combo.parts, option.name.trim()],
          extra: combo.extra + (Number(option.extra) || 0),
          stock: Math.min(combo.stock, Number(option.stock) || 0),
        })
      })
    })
    combos = next
  })
  return combos.map((combo, index) => {
    const name = combo.parts.join(' / ')
    return {
      key: `${name}-${index}`,
      name,
      label: labelOf(name, combo.extra),
      extra: combo.extra,
      stock: stockOf(combo.stock),
    }
  })
})

function isOn(zone: ProductPreviewZone): boolean {
  return props.activeZone === zone
}

function emitEdit(zone: ProductPreviewZone) {
  emit('edit-zone', zone)
}

watch(
  () => props.activeZone,
  async (zone) => {
    if (!zone) return
    await scrollWhenReady(
      () => {
        const root = scrollEl.value
        if (!root) return null
        return root.querySelector(`[data-zone="${zone}"]`) as HTMLElement | null
      },
      {
        behavior: 'auto',
        block: 'center',
        offsetTop: props.embedded ? 130 : 12,
        offsetBottom: 24,
      },
      500,
    )
  },
)
</script>

<style lang="scss" src="./SellerProductPreview.scss"></style>
